import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import OnboardingCard from '../components/OnboardingCard';
import WebhookHealthCard from '../components/admin/WebhookHealthCard';
import DelhiveryWarehouseHealthCard from '../components/admin/DelhiveryWarehouseHealthCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Skeleton } from '../components/ui/skeleton';
import { format } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Users, Store, DollarSign, Activity, Clock, TrendingUp, TrendingDown,
  ShoppingCart, Eye, Star, AlertCircle, ChevronRight, Calendar as CalendarIcon,
  Package, MessageSquare, FileCheck, Wallet, Lightbulb, Target, BarChart3,
  ArrowUpRight, ArrowDownRight, RefreshCw, Smartphone, Play, Pause, Volume2, VolumeX, Bell
} from 'lucide-react';

// Date range presets
const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'last_3_days', label: 'Last 3 Days' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_15_days', label: 'Last 15 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'custom', label: 'Custom Range' }
];

// Sales Analytics Chart Component
const SalesAnalyticsChart = ({ data, period }) => {
  const { formatPrice, currencyInfo } = useCurrency();
  if (!data || data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Sales & Traffic Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-gray-500">
            No analytics data available for this period
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-500" />
          Sales & Traffic Analytics
        </CardTitle>
        <CardDescription>Revenue and visitor trends over time</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 p-4 min-h-0">
        <div className="w-full overflow-x-auto" style={{ minHeight: 220 }}>
          <div style={{ minWidth: data.length > 15 ? `${data.length * 50}px` : '100%' }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }} 
                  tickLine={false}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fontSize: 12 }} 
                  tickLine={false}
                  axisLine={{ stroke: '#e0e0e0' }}
                  tickFormatter={(value) => `${currencyInfo.symbol}${value >= 1000 ? (value/1000).toFixed(1) + 'k' : value}`}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }} 
                  tickLine={false}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'sales') return [`${formatPrice(value)}`, 'Sales'];
                    if (name === 'visits') return [value.toLocaleString(), 'Visits'];
                    if (name === 'leads') return [value.toLocaleString(), 'Leads'];
                    return [value, name];
                  }}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e0e0e0' }}
                />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="sales" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name="Sales"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="visits" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name="Visits"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="leads" 
                  stroke="#f59e0b" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  name="Leads"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Training Academy Video Component
const TrainingVideoCard = ({ apiCall }) => {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);

  const fetchCurrentVideo = useCallback(async () => {
    try {
      const data = await apiCall('/api/training/current-video');
      setCurrentVideo(data.video);
      setProgress(data.progress || 0);
    } catch (error) {
      // Fallback to default/first video if API not available
      setCurrentVideo({
        id: 'intro',
        title: 'Getting Started with Your Vendor Portal',
        url: null, // Will show placeholder
        duration: '5:30',
        description: 'Learn the basics of managing your store'
      });
    }
  }, [apiCall]);

  useEffect(() => {
    fetchCurrentVideo();
  }, [fetchCurrentVideo]);

  const updateProgress = async (currentTime, duration) => {
    const progressPercent = (currentTime / duration) * 100;
    setProgress(progressPercent);
    
    if (progressPercent >= 95 && currentVideo) {
      // Mark as complete and fetch next video
      try {
        await apiCall('/api/training/complete-video', {
          method: 'POST',
          body: JSON.stringify({ video_id: currentVideo.id })
        });
        fetchCurrentVideo(); // Get next video
      } catch (error) {
        console.error('Failed to update video progress:', error);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      updateProgress(videoRef.current.currentTime, videoRef.current.duration);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2 bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardTitle className="text-lg flex items-center gap-2">
          <Play className="h-5 w-5 text-indigo-600" />
          Training Academy
        </CardTitle>
        <CardDescription>Learn how to use your vendor portal effectively</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {currentVideo ? (
          <>
            <div className="relative aspect-video bg-gray-900">
              {currentVideo.url ? (
                // Detect YouTube/Vimeo embed URLs vs direct video files
                currentVideo.url.includes('youtube.com') || currentVideo.url.includes('youtu.be') || currentVideo.url.includes('vimeo.com') ? (
                  <iframe
                    src={(() => {
                      const url = currentVideo.url;
                      // Already an embed URL
                      if (url.includes('/embed/')) return url;
                      // Extract video ID from various YouTube formats
                      let videoId = '';
                      if (url.includes('youtu.be/')) {
                        videoId = url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
                      } else if (url.includes('watch?v=') || url.includes('&v=')) {
                        const match = url.match(/[?&]v=([^&#]+)/);
                        videoId = match ? match[1] : '';
                      }
                      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                      // Vimeo or other — return as-is
                      return url;
                    })()}
                    className="w-full h-full"
                    title={currentVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    ref={videoRef}
                    src={currentVideo.url}
                    className="w-full h-full"
                    poster={currentVideo.thumbnail}
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    muted={isMuted}
                    controls
                  />
                )
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-white">
                  <Play className="h-16 w-16 mb-4 opacity-50" />
                  <p className="text-lg font-medium">{currentVideo.title}</p>
                  <p className="text-sm text-gray-400 mt-1">Video coming soon</p>
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium">{currentVideo.title}</h4>
                <span className="text-sm text-gray-500">{currentVideo.duration}</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{currentVideo.description}</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">{Math.round(progress)}% complete</p>
              <a href="/vendor/training" className="text-xs text-indigo-600 hover:text-indigo-800 mt-2 inline-block font-medium">
                View All Training &rarr;
              </a>
            </div>
          </>
        ) : (
          <div className="p-8 text-center">
            <Skeleton className="aspect-video w-full mb-4" />
            <Skeleton className="h-4 w-3/4 mx-auto" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// New Order Notification Card
const NewOrdersNotificationCard = ({ orders, onViewAll, onMarkSeen }) => {
  const [playSound, setPlaySound] = useState(true);
  const audioRef = useRef(null);
  const { formatPrice } = useCurrency();
  const hasNewOrders = orders && orders.length > 0;

  useEffect(() => {
    // Play notification sound for new orders
    if (hasNewOrders && playSound && audioRef.current) {
      audioRef.current.play().catch(() => {
        // Autoplay blocked - user needs to interact first
      });
    }
  }, [hasNewOrders, playSound]);

  if (!hasNewOrders) return null;

  return (
    <Card className="border-2 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg animate-pulse-once">
      <audio ref={audioRef} src="/sounds/order-notification.mp3" preload="auto" />
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2 text-green-700">
            <Bell className="h-5 w-5 animate-bounce" />
            {orders.length} New Order{orders.length > 1 ? 's' : ''}!
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setPlaySound(!playSound)}
              className="h-8 w-8"
            >
              {playSound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {orders.slice(0, 5).map((order, idx) => (
            <div key={order.id || idx} className="flex items-center justify-between p-2 bg-white rounded-lg border border-green-200">
              <div>
                <p className="font-medium text-sm">{order.order_number || `Order #${idx + 1}`}</p>
                <p className="text-xs text-gray-600">{order.customer_name || 'Customer'}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-green-600">{formatPrice(order.total_amount || 0)}</p>
                <p className="text-xs text-gray-500">{order.created_at ? format(new Date(order.created_at), 'h:mm a') : 'Just now'}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={onViewAll}>
            View All Orders
          </Button>
          {onMarkSeen && (
            <Button variant="outline" onClick={onMarkSeen}>
              Mark as Seen
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Stat Card with trend indicator
const StatCard = ({ title, value, icon: Icon, trend, subtitle, onClick }) => {
  const getTrendColor = () => {
    if (!trend) return 'text-gray-500';
    return trend.direction === 'up' ? 'text-green-600' : 
           trend.direction === 'down' ? 'text-red-600' : 'text-gray-500';
  };

  const TrendIcon = trend?.direction === 'up' ? ArrowUpRight : 
                    trend?.direction === 'down' ? ArrowDownRight : null;

  return (
    <Card className={`${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={onClick}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {trend && (
              <div className={`flex items-center mt-1 text-sm ${getTrendColor()}`}>
                {TrendIcon && <TrendIcon className="h-4 w-4 mr-1" />}
                <span>{trend.percentage}%</span>
                <span className="text-gray-500 ml-1">vs prev period</span>
              </div>
            )}
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <Icon className="h-6 w-6 text-blue-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Pending Actions Card
const PendingActionsCard = ({ actions, onActionClick }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-orange-500" />
          Pending Actions
        </CardTitle>
        <CardDescription>Items requiring your attention</CardDescription>
      </CardHeader>
      <CardContent>
        {actions.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No pending actions</p>
        ) : (
          <div className="space-y-3">
            {actions.map((action, idx) => (
              <div
                key={action.action_url || action.title || `action-${idx}`}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                onClick={() => onActionClick(action.action_url)}
              >
                <div className="flex items-center gap-3">
                  <Badge className={getPriorityColor(action.priority)}>
                    {action.count}
                  </Badge>
                  <span className="font-medium text-gray-900">{action.title}</span>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Top Items List Card
const TopItemsCard = ({ title, icon: Icon, items, columns, emptyMessage }) => {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className="h-5 w-5 text-blue-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-gray-500 text-center py-4">{emptyMessage || 'No data available'}</p>
        ) : (
          <div className="space-y-3">
            {items.map((item, idx) => (
              <div key={item.id || item[columns.title] || `item-${idx}`} className="flex items-center justify-between py-2 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 w-6">#{idx + 1}</span>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{item[columns.title]}</p>
                    {columns.subtitle && (
                      <p className="text-xs text-gray-500">{item[columns.subtitle]}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    {columns.valuePrefix}{item[columns.value]?.toLocaleString()}{columns.valueSuffix}
                  </p>
                  {columns.secondaryValue && (
                    <p className="text-xs text-gray-500">
                      {item[columns.secondaryValue]} {columns.secondaryLabel}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Recent Transactions Card
const RecentTransactionsCard = ({ transactions, onViewAll }) => {
  const { formatPrice } = useCurrency();
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'delivered':
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending':
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'refunded': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-green-500" />
            Recent Transactions
          </CardTitle>
        </div>
        {onViewAll && (
          <Button variant="ghost" size="sm" onClick={onViewAll}>
            View All <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {transactions.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No recent transactions</p>
        ) : (
          <div className="space-y-3">
            {transactions.map((tx, idx) => (
              <div key={tx.id || tx.order_id || `tx-${idx}`} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {tx.order_id || tx.id?.slice(0, 8)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {tx.customer_name || 'Customer'}
                  </p>
                </div>
                <div className="text-right">
                  {tx.total_amount && (
                    <p className="font-semibold text-gray-900">{formatPrice(tx.total_amount)}</p>
                  )}
                  <Badge className={getStatusColor(tx.status)} variant="secondary">
                    {tx.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Suggestions Card
const SuggestionsCard = ({ suggestions, onViewSuggestion }) => {
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'medium': return <Lightbulb className="h-5 w-5 text-yellow-500" />;
      default: return <Target className="h-5 w-5 text-blue-500" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'seo': return 'bg-purple-100 text-purple-800';
      case 'marketing': return 'bg-blue-100 text-blue-800';
      case 'engagement': return 'bg-green-100 text-green-800';
      case 'revenue': return 'bg-yellow-100 text-yellow-800';
      case 'compliance': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-yellow-500" />
          Recommendations
        </CardTitle>
        <CardDescription>Actions to improve your marketplace performance</CardDescription>
      </CardHeader>
      <CardContent>
        {suggestions.length === 0 ? (
          <p className="text-gray-500 text-center py-4">Great job! No recommendations at this time.</p>
        ) : (
          <div className="space-y-3">
            {suggestions.map((suggestion, idx) => (
              <div
                key={suggestion.id || suggestion.title || `suggestion-${idx}`}
                className="bg-white p-4 rounded-lg border border-blue-100 hover:border-blue-300 cursor-pointer transition-colors"
                onClick={() => onViewSuggestion(suggestion.action_url)}
              >
                <div className="flex items-start gap-3">
                  {getPriorityIcon(suggestion.priority)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900">{suggestion.title}</p>
                      <Badge className={getCategoryColor(suggestion.category)} variant="secondary">
                        {suggestion.category}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{suggestion.description}</p>
                    {suggestion.metric_trigger && (
                      <p className="text-xs text-gray-500 mt-1 italic">
                        Triggered by: {suggestion.metric_trigger}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Ad Campaigns ROI Card
const AdCampaignsCard = ({ campaigns }) => {
  const { formatPrice } = useCurrency();
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-purple-500" />
          Ad Campaigns ROI
        </CardTitle>
      </CardHeader>
      <CardContent>
        {campaigns.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No active campaigns</p>
        ) : (
          <div className="space-y-3">
            {campaigns.map((campaign, idx) => (
              <div key={campaign.id || campaign.name || `campaign-${idx}`} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-gray-900">{campaign.name}</p>
                  <Badge className={campaign.roi >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    ROI: {campaign.roi >= 0 ? '+' : ''}{campaign.roi}%
                  </Badge>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <p className="text-gray-500">Spent</p>
                    <p className="font-medium">{formatPrice(campaign.spent)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Impressions</p>
                    <p className="font-medium">{campaign.impressions?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Clicks</p>
                    <p className="font-medium">{campaign.clicks?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Revenue</p>
                    <p className="font-medium">{formatPrice(campaign.revenue)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
const Dashboard = ({ onToggleLiteMode }) => {
  const { isAdmin, user, apiCall, loading: authLoading, clearForbiddenBackoff } = useAuth();
  const { formatPrice, currencyInfo } = useCurrency();
  const [datePreset, setDatePreset] = useState('last_30_days');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);

  const navigate = (path) => {
    window.location.href = path;
  };

  const fetchDashboardData = useCallback(async () => {
    // iter-230ak — Guard against firing during AuthContext hydration.
    // Without this guard the dashboard fired 3× (mount → setUser →
    // setToken) × 2 parallel calls = 6 hits during boot, producing
    // the visible 403 "storm" when Cloudflare challenged the burst.
    if (authLoading || !user) {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const endpoint = isAdmin() 
        ? '/api/dashboard/admin/stats' 
        : '/api/dashboard/vendor/stats';

      let params = `?preset=${datePreset}`;
      if (datePreset === 'custom' && customDateRange.from && customDateRange.to) {
        params = `?preset=custom&start_date=${customDateRange.from.toISOString()}&end_date=${customDateRange.to.toISOString()}`;
      }

      const [statsResponse, suggestionsResponse] = await Promise.all([
        apiCall(`${endpoint}${params}`),
        apiCall(`/api/dashboard/suggestions?role=${isAdmin() ? 'admin' : 'vendor'}`)
      ]);

      setDashboardData(statsResponse);
      setSuggestions(suggestionsResponse.suggestions || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(
        err?.code === 'FORBIDDEN' || err?.code === 'FORBIDDEN_BACKOFF'
          ? 'This request was blocked. If you have an ad blocker, VPN, or strict network filter enabled, disable it and retry.'
          : 'Failed to load dashboard data. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [isAdmin, datePreset, customDateRange?.from?.getTime(), customDateRange?.to?.getTime(), apiCall, authLoading, user]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // iter-230ak — Explicit retry: clear the 403 backoff for this URL
  // family, then refetch. Wired into the error banner's Retry button.
  const handleRetryFetch = useCallback(() => {
    try {
      clearForbiddenBackoff('/api/dashboard/vendor/stats');
      clearForbiddenBackoff('/api/dashboard/admin/stats');
      clearForbiddenBackoff('/api/dashboard/suggestions');
    } catch (_) { /* ignore */ }
    fetchDashboardData();
  }, [clearForbiddenBackoff, fetchDashboardData]);

  const handleDatePresetChange = (value) => {
    setDatePreset(value);
    if (value === 'custom') {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  };

  const handleCustomDateSelect = (range) => {
    setCustomDateRange(range);
    if (range.from && range.to) {
      setShowDatePicker(false);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-10 w-40" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i.id || i.name || `${i.title || "i"}-${i}`}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error) {
    // Dashboard data fetch failed (currently a known /api/dashboard issue),
    // BUT we still want admins to be able to access the maintenance widgets
    // (webhook health + Delhivery warehouse registration) without first
    // having a working dashboard. Render a minimal layout with both cards
    // above the error block instead of the bare retry-only screen.
    return (
      <DashboardLayout>
        <div className="space-y-4">
          {isAdmin() && <WebhookHealthCard />}
          {isAdmin() && <DelhiveryWarehouseHealthCard />}
          <div className="flex flex-col items-center justify-center py-12 border border-dashed border-red-200 rounded-lg bg-red-50/30">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={handleRetryFetch} data-testid="dashboard-retry-button">
              <RefreshCw className="h-4 w-4 mr-2" /> Retry
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const overview = dashboardData?.overview || {};

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header with Date Range Selector */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isAdmin() ? 'Admin Dashboard' : 'Vendor Dashboard'}
            </h1>
            <p className="text-gray-600 mt-1">
              Welcome back, {user?.name}! Here&apos;s your business overview.
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Lite Mode Toggle for Vendors - Mobile Only */}
            {!isAdmin() && onToggleLiteMode && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onToggleLiteMode}
                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 lg:hidden"
              >
                <Smartphone className="h-4 w-4 mr-1" />
                Lite Mode
              </Button>
            )}
            
            <Select value={datePreset} onValueChange={handleDatePresetChange}>
              <SelectTrigger className="w-[180px]">
                <CalendarIcon className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {DATE_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {datePreset === 'custom' && (
              <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    {customDateRange.from && customDateRange.to
                      ? `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d')}`
                      : 'Pick dates'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={customDateRange}
                    onSelect={handleCustomDateSelect}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            )}

            <Button variant="outline" size="icon" onClick={fetchDashboardData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Vendor Onboarding Card - Prominently displayed for new vendors until they complete setup */}
        {user?.role === 'vendor' && (
          <OnboardingCard />
        )}

        {/* Overview Stats */}
        {isAdmin() ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              title="Total Vendors"
              value={overview.vendors?.total || 0}
              icon={Users}
              trend={overview.vendors?.trend}
              subtitle={`${overview.vendors?.active || 0} active`}
            />
            <StatCard
              title="Total Listings"
              value={overview.listings?.total || 0}
              icon={Store}
              trend={overview.listings?.trend}
              subtitle={`${overview.listings?.active || 0} active`}
            />
            <StatCard
              title="Revenue"
              value={formatPrice(overview.revenue?.total || 0)}
              icon={DollarSign}
              trend={overview.revenue?.trend}
            />
            <StatCard
              title="Customers"
              value={overview.customers?.total || 0}
              icon={Users}
              trend={overview.customers?.trend}
              subtitle={`${overview.customers?.new || 0} new`}
            />
            <StatCard
              title="Orders"
              value={overview.orders?.total || 0}
              icon={ShoppingCart}
              trend={overview.orders?.trend}
            />
          </div>
        ) : (          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <StatCard
              title="Listings"
              value={overview.listings?.total || 0}
              icon={Store}
              subtitle={`${overview.listings?.active || 0} active`}
            />
            <StatCard
              title="Revenue"
              value={formatPrice(overview.revenue?.total || 0)}
              icon={DollarSign}
              trend={overview.revenue?.trend}
            />
            <StatCard
              title="Orders"
              value={overview.orders?.total || 0}
              icon={ShoppingCart}
              trend={overview.orders?.trend}
            />
            <StatCard
              title="Views"
              value={(overview.views?.total || 0).toLocaleString()}
              icon={Eye}
              trend={overview.views?.trend}
            />
            <StatCard
              title="Bookings"
              value={overview.bookings?.total || 0}
              icon={Clock}
              trend={overview.bookings?.trend}
            />
            <StatCard
              title="Rating"
              value={overview.rating?.average || 0}
              icon={Star}
              subtitle={`${overview.rating?.count || 0} reviews`}
            />
          </div>
        )}

        {/* Admin-only: Shopify Webhook auto-capture health */}
        {isAdmin() && <WebhookHealthCard />}
        {isAdmin() && <DelhiveryWarehouseHealthCard />}

        {/* NEW: Vendor-specific sections - New Orders, Sales Chart, Training Video */}
        {!isAdmin() && (
          <>
            {/* New Orders Notification Card */}
            <NewOrdersNotificationCard
              orders={dashboardData?.new_orders || []}
              onViewAll={() => navigate('/orders')}
              onMarkSeen={async () => {
                try {
                  await apiCall('/api/dashboard/vendor/mark-orders-seen', { method: 'POST' });
                  fetchDashboardData(); // Refresh to clear notification
                } catch (error) {
                  console.error('Failed to mark orders as seen:', error);
                }
              }}
            />

            {/* Sales Analytics Chart & Training Video Row */}
            {/* Sales Analytics Chart & Training Video Row - Equal heights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[400px]">
              <SalesAnalyticsChart 
                data={dashboardData?.chart_data || []} 
                period={datePreset}
              />
              <TrainingVideoCard apiCall={apiCall} />
            </div>
          </>
        )}

        {/* Pending Actions & Top Performers Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PendingActionsCard
            actions={dashboardData?.pending_actions || []}
            onActionClick={navigate}
          />
          
          {isAdmin() ? (
            <TopItemsCard
              title="Top Vendors"
              icon={Users}
              items={dashboardData?.top_vendors || []}
              columns={{
                title: 'name',
                subtitle: 'email',
                value: 'revenue',
                valuePrefix: currencyInfo.symbol,
                secondaryValue: 'orders',
                secondaryLabel: 'orders'
              }}
              emptyMessage="No vendor data for this period"
            />
          ) : (
            <TopItemsCard
              title="Top Products"
              icon={Package}
              items={dashboardData?.top_products || []}
              columns={{
                title: 'title',
                subtitle: 'type',
                value: 'revenue',
                valuePrefix: currencyInfo.symbol,
                secondaryValue: 'sales',
                secondaryLabel: 'sales'
              }}
              emptyMessage="No product data for this period"
            />
          )}
        </div>

        {/* Second Row - Products/Campaigns & Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {isAdmin() ? (
            <>
              <TopItemsCard
                title="Top Products"
                icon={Package}
                items={dashboardData?.top_products || []}
                columns={{
                  title: 'title',
                  subtitle: 'type',
                  value: 'revenue',
                  valuePrefix: currencyInfo.symbol,
                  secondaryValue: 'sales',
                  secondaryLabel: 'sales'
                }}
                emptyMessage="No product data for this period"
              />
              <AdCampaignsCard campaigns={dashboardData?.ad_campaigns || []} />
            </>
          ) : (
            <>
              <RecentTransactionsCard
                transactions={dashboardData?.recent_orders || []}
                onViewAll={() => navigate('/orders')}
              />
              <TopItemsCard
                title="Customer Requests"
                icon={MessageSquare}
                items={dashboardData?.customer_requests || []}
                columns={{
                  title: 'request_type',
                  subtitle: 'customer_name',
                  value: 'status'
                }}
                emptyMessage="No customer requests"
              />
            </>
          )}
        </div>

        {/* Third Row - Transactions & Compliance/Payouts */}
        {isAdmin() && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <RecentTransactionsCard
              transactions={dashboardData?.recent_transactions || []}
              onViewAll={() => navigate('/orders')}
            />
            <TopItemsCard
              title="Customer Requests"
              icon={MessageSquare}
              items={dashboardData?.recent_customer_requests || []}
              columns={{
                title: 'request_type',
                subtitle: 'customer_name',
                value: 'status'
              }}
              emptyMessage="No customer requests"
            />
            <TopItemsCard
              title="Pending Compliance"
              icon={FileCheck}
              items={dashboardData?.pending_compliance || []}
              columns={{
                title: 'compliance_type',
                subtitle: 'vendor_name',
                value: 'status'
              }}
              emptyMessage="All compliance up to date"
            />
          </div>
        )}

        {/* Payouts Section (Vendor) */}
        {!isAdmin() && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-green-500" />
                  Earnings Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-600">Total Earned</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatPrice(overview.earnings?.total || 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-gray-600">Pending Payout</p>
                    <p className="text-2xl font-bold text-yellow-700">
                      {formatPrice(overview.earnings?.pending || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <TopItemsCard
              title="Recent Payouts"
              icon={Wallet}
              items={dashboardData?.payouts || []}
              columns={{
                title: 'status',
                subtitle: 'created_at',
                value: 'amount',
                valuePrefix: currencyInfo.symbol
              }}
              emptyMessage="No payout history"
            />
          </div>
        )}

        {/* Recent Payouts (Admin) */}
        {isAdmin() && (
          <div className="grid grid-cols-1">
            <TopItemsCard
              title="Recent Payouts"
              icon={Wallet}
              items={dashboardData?.recent_payouts || []}
              columns={{
                title: 'vendor_name',
                subtitle: 'status',
                value: 'amount',
                valuePrefix: currencyInfo.symbol
              }}
              emptyMessage="No recent payouts"
            />
          </div>
        )}

        {/* Suggestions Section */}
        <SuggestionsCard
          suggestions={suggestions}
          onViewSuggestion={navigate}
        />
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
